import { FormEvent, useState, useEffect } from 'react';
import { Col, Container, Form, FormGroup, Input, Label, Row, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Btn, H2 } from '../../../../AbstractElements';
import { Password } from '../../../../Utils/Constants';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';
import { crmLoginService } from '../../../../Services/Auth';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';
import { setAdminData } from '../../../../ReduxToolkit/Reducers/AdminAuthSlice';
import { useSafeNavigate } from '../../../../Hooks/useSafeNavigate';
import AppFooter from '../../../../CommonElements/AppFooter';

export default function CRMLogin() {
    const navigate = useSafeNavigate();
    const dispatch = useDispatch();
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Set by the axios response interceptor (axiosApi.ts) when any API call
    // comes back 401 - the user's session expired server-side while they
    // were still sitting on a screen, so they get redirected here with this
    // flag instead of just seeing blank/broken data.
    const [searchParams, setSearchParams] = useSearchParams();
    const sessionExpired = searchParams.get('sessionExpired') === '1';

    useEffect(() => {
        const policyAgree = localStorage.getItem('policyAgree');
        if (policyAgree === 'true') {
            setIsModalOpen(false);
        }
    }, []);

    // Clear the flag from the URL once shown, so refreshing the login page
    // (or logging in and later hitting back) doesn't keep re-showing it.
    useEffect(() => {
        if (sessionExpired) {
            const next = new URLSearchParams(searchParams);
            next.delete('sessionExpired');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);
    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleCancel = () => {
        toggleModal();
    };

    const { mutate: login, isPending } = useMutationRequestQuery(
        'crmLoginService',
        crmLoginService,
        {
            onSuccess: (data: any) => {
                const decodedToken: any = jwtDecode(data?.data?.token);
                const sessionTime = decodedToken?.exp * 1000;

                localStorage.setItem('crmtoken', data?.data?.token);
                localStorage.setItem('policyAgree', 'true');
                localStorage.setItem('crmsessiontime', sessionTime.toString());

                dispatch(setAdminData({
                    nameidentifier: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
                    sub: decodedToken['sub'],
                    email: decodedToken['email'],
                    jti: decodedToken['jti'],
                    ContactNo: decodedToken['ContactNo'],
                    RoleID: decodedToken['RoleID'],
                    RoleName: decodedToken['RoleName'],
                    FirstName: decodedToken['FirstName'],
                    LastName: decodedToken['LastName'],
                    aud: decodedToken['aud'],
                    token: data?.data?.token,
                }));

                toast.success("Login successful!");
                handleCancel();

                // Navigate based on RoleID
        
                if (decodedToken['RoleID'] == 7) {
                    navigate(`${process.env.PUBLIC_URL}/CRMOperatorMembers`);
                } else {
                    navigate(`${process.env.PUBLIC_URL}/CRMDashboard`);
                }
            },
            onError: (error: any) => {
                toast.error("Login failed. Please check your credentials.");
            },
        }
    );

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        login({ email, password });
    };

    return (
        <Container className="p-0" fluid>
            <Row className="m-0">
                <Col xs={12} className="p-0">
                    <div className="login-card login-dark">
                        <div>
                            <div className="login-main">
                                <Form className="theme-form" onSubmit={handleSubmit}>
                                    <div className='text-center'>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/assets/images/fintrack_logo.png`}
                                            alt="Fintrack Logo"
                                            style={{ maxWidth: '230px', height: 'auto' }}
                                        />
                                    </div>
                                    <H2 className="text-center heading-color my-4">CRM Login</H2>
                                    {sessionExpired && (
                                        <Alert color="warning" className="text-center">
                                            Your session has timed out. Please log in again.
                                        </Alert>
                                    )}
                                    <FormGroup>
                                        <Col><Label>Email</Label></Col>
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            required
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Col><Label>{Password}</Label></Col>
                                        <div className="form-input position-relative">
                                            <Input
                                                type={isPasswordVisible ? 'text' : 'password'}
                                                value={password}
                                                onChange={(event) => setPassword(event.target.value)}
                                                name="login[password]"
                                                required
                                            />
                                            <div className='show-hide' onClick={togglePasswordVisibility}>
                                                {isPasswordVisible ? <FiEye style={{ cursor: 'pointer' }} size={20} /> : <FiEyeOff style={{ cursor: 'pointer' }} size={20} />}
                                            </div>
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="mb-0 checkbox-checked">
                                        <div className="text-end mt-3">
                                            <Btn color='primary' className="w-100" block type="submit" disabled={isPending}>{isPending ? "Loading . . ." : "Login"}</Btn>
                                        </div>
                                    </FormGroup>
                                    <div style={{ fontSize: '12px', textAlign: 'center' }}>
                                        <span>Copyright - {new Date().getFullYear()} - Fintrack Pty Ltd ACN 006 164 709.</span>
                                        <br />
                                        <span>All rights reserved.</span>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <AppFooter />
        </Container>
    );
}